import { FragmentType, gql, useFragment } from '@/__generated__';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import {
  getPostWeekDays,
  isDefaultPostingTime,
  isGeneralDefaultPostingTime,
  isResetButtonDisabled,
  transformScheduleArray,
  transformScheduling,
} from './posting-times.util';
import { useState } from 'react';
import {
  PostChannel,
  SetSocialMediaScheduleInput,
  SocialMediaScheduling,
} from '@/__generated__/graphql';
import { ApolloError, useMutation } from '@apollo/client';
import { useToast } from '@/components/shadcn/use-toast';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/shadcn/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/shadcn/accordion';

gql(`
  fragment SocialMediaScheduling on SocialMediaScheduling {
    Facebook {
      Days {
        Friday
        Monday
        Saturday
        Sunday
        Thursday
        Tuesday
        Wednesday
      }
      GeneralDefault {
        EventTime
      }
    }
    Instagram {
      Days {
        Friday
        Monday
        Saturday
        Sunday
        Thursday
        Tuesday
        Wednesday
      }
      GeneralDefault {
        EventTime
      }
    }
    LinkedIn {
      Days {
        Friday
        Monday
        Saturday
        Sunday
        Thursday
        Tuesday
        Wednesday
      }
      GeneralDefault {
        EventTime
      }
    }
  }
`);

const ChannelPostingTimesFragment = gql(`
  fragment ChannelPostingTimesFragment on SchedulingVariantV1 {
    id
    socialMediaSchedulingDefaults {
      ...SocialMediaScheduling
    }
    socialMediaScheduling {
      ...SocialMediaScheduling
    }
    socialMediaSchedulingOverride {
      ...SocialMediaScheduling
    }
  }
`);

const MutationSetSocialMediaScheduling = gql(`
  mutation SetSocialMediaScheduling(
    $input: SetSocialMediaSchedulingInput!
    $opt: SetSocialMediaSchedulingOptions
  ) {
    schedulingVariantV1 {
      socialMediaScheduling {
        set(input: $input, opt: $opt) {
          ... on SetSocialMediaSchedulingResult {
            result {
              id
              ...ChannelPostingTimesFragment
            }
          }
        }
      }
    }
  }
`);

const MutationUnsetSocialMediaScheduling = gql(`
  mutation UnsetSocialMediaScheduling(
    $input: UnsetSocialMediaSchedulingInput!
    $opt: UnsetSocialMediaSchedulingOptions
  ) {
    schedulingVariantV1 {
      socialMediaScheduling {
        unset(input: $input, opt: $opt) {
          ... on UnsetSocialMediaSchedulingResult {
            result {
              id
              ...ChannelPostingTimesFragment
            }
          }
        }
      }
    }
  }
`);

export default function ChannelPostingTimes({
  variant,
  channel,
}: {
  variant: FragmentType<typeof ChannelPostingTimesFragment>;
  channel: Exclude<PostChannel, PostChannel.Unspecified>;
}) {
  const postingTimes = useFragment(ChannelPostingTimesFragment, variant);
  const [postingTimesState, setPostingTimesState] = useState<
    SocialMediaScheduling | null | undefined
  >(postingTimes?.socialMediaScheduling);
  const { t } = useTranslation();
  const { toast } = useToast();

  const [updateSocialMediaTimes] = useMutation(
    MutationSetSocialMediaScheduling,
    {
      onCompleted: () => {
        toast({
          description: 'Posting times saved successfuly',
          duration: 3000,
        });
      },
      onError: (error: ApolloError) => {
        toast({
          description: error.message,
          variant: 'destructive',
          duration: 3000,
        });
      },
    }
  );

  const onSave = async (
    channel: Exclude<PostChannel, PostChannel.Unspecified>
  ) => {
    const transformedData = transformScheduling(
      postingTimesState && postingTimesState[channel],
      postingTimes?.socialMediaScheduling &&
        (postingTimes.socialMediaScheduling as SocialMediaScheduling)[channel],
      channel,
      postingTimesState && postingTimesState[channel]?.GeneralDefault?.EventTime
    );
    await updateSocialMediaTimes({
      variables: {
        input: {
          variantId: postingTimes?.id || '',
          schedules: transformedData as SetSocialMediaScheduleInput[],
        },
        opt: {
          syncFuturePosts: true,
        },
      },
    });
  };

  const [resetSocialMediaTimes] = useMutation(
    MutationUnsetSocialMediaScheduling,
    {
      onCompleted: (data, variables) => {
        const channel: Exclude<PostChannel, PostChannel.Unspecified> =
          variables?.variables?.input.schedules[0].platform;

        const platformData =
          postingTimes?.socialMediaSchedulingDefaults &&
          (
            postingTimes?.socialMediaSchedulingDefaults as SocialMediaScheduling
          )[channel];

        setPostingTimesState(
          (prevState: SocialMediaScheduling | null | undefined) => ({
            ...prevState,
            [channel]: platformData,
          })
        );

        toast({
          description: 'Posting times reset successfuly',
          duration: 3000,
        });
      },
      onError: (error: ApolloError) => {
        toast({
          description: error.message,
          variant: 'destructive',
          duration: 3000,
        });
      },
    }
  );

  const onResetAll = async (channel: PostChannel) => {
    const days = getPostWeekDays(
      channel,
      postingTimes?.socialMediaSchedulingOverride
    );
    if (days) {
      await resetSocialMediaTimes({
        variables: {
          input: {
            variantId: postingTimes?.id || '',
            schedules: [
              {
                platform: channel,
                days: days,
              },
              {
                platform: channel,
                days: [],
                generalDefault: {
                  eventTime: true,
                },
              },
            ],
          },
          opt: {
            syncFuturePosts: true,
          },
        },
      });
    }
  };

  const onTimesChange = (
    time: string | null,
    channel: Exclude<PostChannel, PostChannel.Unspecified>,
    day: string,
    isGeneralDefaultPostingTime?: boolean
  ) => {
    const newState = { ...postingTimesState };
    const platformData = newState[channel];

    if (isGeneralDefaultPostingTime) {
      const updatedPlatformData = {
        ...platformData,
        Days: {
          ...platformData?.Days,
        },
        GeneralDefault: {
          EventTime: time,
        },
      };

      newState[channel] = updatedPlatformData;
    } else {
      const updatedPlatformData = {
        ...platformData,
        Days: {
          ...platformData?.Days,
          [day]: time,
        },
      };

      newState[channel] = updatedPlatformData;
    }

    setPostingTimesState(newState);
  };

  return (
    <div>
      <Accordion type='multiple'>
        <AccordionItem value='1'>
          <AccordionTrigger aria-controls='panel1-content' id='panel1-header'>
            {channel}
          </AccordionTrigger>
          <AccordionContent>
            <div className='mb-2 mt-2'>
              {postingTimes?.socialMediaSchedulingDefaults &&
                (
                  postingTimes?.socialMediaSchedulingDefaults as SocialMediaScheduling
                )[channel]?.Days && (
                  <div className='mb-2 mr-4 flex items-center justify-end gap-2'>
                    <Button
                      variant={'maggie-primary'}
                      onClick={() => onResetAll(channel)}
                      disabled={isResetButtonDisabled(
                        channel,
                        postingTimesState,
                        postingTimes?.socialMediaSchedulingDefaults as SocialMediaScheduling
                      )}
                    >
                      {t('post-client.reset-all')}
                    </Button>
                  </div>
                )}
              <p className='mb-2 font-medium'>{t('post-client.days')}</p>

              {postingTimesState &&
                transformScheduleArray(postingTimesState[channel]?.Days)?.map(
                  ({ day, hour }) => (
                    <div className='mb-2 flex items-center gap-2' key={day}>
                      <p className='mb-2 min-w-[120px]'>{day}</p>
                      <div className='relative w-full'>
                        <TimePicker
                          value={hour as string}
                          disableClock
                          onChange={(value) => {
                            onTimesChange(value, channel, day);
                          }}
                          format='HH:mm'
                          clearIcon={null}
                          clockIcon={null}
                          className={cn(
                            'w-full p-1 pr-4',
                            isDefaultPostingTime(
                              channel,
                              day,
                              postingTimesState,
                              postingTimes?.socialMediaSchedulingDefaults
                            )
                              ? 'opacity-50'
                              : 'opacity-100'
                          )}
                        />
                      </div>
                    </div>
                  )
                )}
              <p className='mb-1 font-medium'>{t('post-client.general')}</p>
              <div className='mb-5 flex items-center gap-2'>
                <p className='mb-1 min-w-[120px]'>{t('post-client.events')}</p>
                <TimePicker
                  value={
                    postingTimesState &&
                    postingTimesState[channel]?.GeneralDefault?.EventTime
                  }
                  disableClock
                  onChange={(value) => {
                    onTimesChange(value, channel, '', true);
                  }}
                  format='HH:mm'
                  clearIcon={null}
                  clockIcon={null}
                  className={cn(
                    'w-full p-1 pr-4',
                    isGeneralDefaultPostingTime(
                      channel,
                      postingTimesState,
                      postingTimes?.socialMediaSchedulingDefaults as SocialMediaScheduling
                    )
                      ? 'opacity-50'
                      : 'opacity-100'
                  )}
                />
              </div>
              <div className='mb-2 flex items-center gap-2'>
                <Button
                  variant={'maggie-primary'}
                  onClick={() => onSave(channel)}
                >
                  {t('common.save')}
                </Button>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
